// components/products/sections/StoneSection.jsx
import React from "react";
import { Plus, Trash2 } from "lucide-react";

const StoneSection = ({
  formData,
  addStone,
  updateStone,
  removeStone,
  STONE_TYPE_ENUM,
  STONE_SHAPE_ENUM,
  STONE_SETTING_ENUM,
  errors,
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Stone / Diamond Information
        </h3>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.hasStones}
            onChange={(e) => {
              if (e.target.checked && formData.stones.length === 0) {
                addStone();
              }
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Has Stones</span>
        </label>
      </div>

      {formData.hasStones && (
        <>
          {/* Stone Summary */}
          {formData.stones.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded">
              <div>
                <span className="text-xs text-gray-600">Total Stones</span>
                <p className="text-lg font-semibold">
                  {formData.stones.reduce((sum, s) => sum + (s.count || 1), 0)}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-600">Total Weight (ct)</span>
                <p className="text-lg font-semibold">
                  {formData.totalStoneWeight.toFixed(3)}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-600">Total Value</span>
                <p className="text-lg font-semibold">
                  ₹{formData.stoneCharges.toLocaleString()}
                </p>
              </div>
            </div>
          )}

          {/* Stones List */}
          <div className="space-y-4">
            {formData.stones.map((stone, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">
                    Stone #{index + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={() => removeStone(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Stone Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stone Type *
                    </label>
                    <select
                      value={stone.type}
                      onChange={(e) =>
                        updateStone(index, "type", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      {STONE_TYPE_ENUM.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Shape */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shape
                    </label>
                    <select
                      value={stone.shape}
                      onChange={(e) =>
                        updateStone(index, "shape", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      {STONE_SHAPE_ENUM.map((shape) => (
                        <option key={shape} value={shape}>
                          {shape}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Setting Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Setting Type
                    </label>
                    <select
                      value={stone.settingType}
                      onChange={(e) =>
                        updateStone(index, "settingType", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      {STONE_SETTING_ENUM.map((setting) => (
                        <option key={setting} value={setting}>
                          {setting}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weight (carats) *
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      value={stone.weight || ""}
                      onChange={(e) =>
                        updateStone(
                          index,
                          "weight",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.000"
                    />
                  </div>

                  {/* Count */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Count
                    </label>
                    <input
                      type="number"
                      value={stone.count || 1}
                      onChange={(e) =>
                        updateStone(
                          index,
                          "count",
                          parseInt(e.target.value) || 1
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                    />
                  </div>

                  {/* Rate per Carat */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate per Carat (₹)
                    </label>
                    <input
                      type="number"
                      value={stone.ratePerCarat || ""}
                      onChange={(e) =>
                        updateStone(
                          index,
                          "ratePerCarat",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Diamond specific fields */}
                  {stone.type === "DIAMOND" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color
                        </label>
                        <input
                          type="text"
                          value={stone.color || ""}
                          onChange={(e) =>
                            updateStone(index, "color", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="D, E, F..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Clarity
                        </label>
                        <input
                          type="text"
                          value={stone.clarity || ""}
                          onChange={(e) =>
                            updateStone(index, "clarity", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="VVS1, VS1..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cut
                        </label>
                        <input
                          type="text"
                          value={stone.cut || ""}
                          onChange={(e) =>
                            updateStone(index, "cut", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Excellent, Very Good..."
                        />
                      </div>
                    </>
                  )}

                  {/* Total Value (calculated) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Value
                    </label>
                    <input
                      type="text"
                      value={`₹${(stone.totalValue || 0).toLocaleString()}`}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                </div>

                {/* Certification for individual stone */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Stone Certification (Optional)
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Agency
                      </label>
                      <input
                        type="text"
                        value={stone.certification?.agency || ""}
                        onChange={(e) =>
                          updateStone(
                            index,
                            "certification.agency",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                        placeholder="GIA, IGI..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Certificate Number
                      </label>
                      <input
                        type="text"
                        value={stone.certification?.certificateNumber || ""}
                        onChange={(e) =>
                          updateStone(
                            index,
                            "certification.certificateNumber",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                        placeholder="Certificate #"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Certificate URL
                      </label>
                      <input
                        type="text"
                        value={stone.certification?.certificateUrl || ""}
                        onChange={(e) =>
                          updateStone(
                            index,
                            "certification.certificateUrl",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addStone}
              className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Stone
            </button>
          </div>

          {errors.stones && (
            <p className="mt-2 text-sm text-red-600">{errors.stones}</p>
          )}
        </>
      )}
    </div>
  );
};

export default StoneSection;
